<template>
  <div class='w-full h-full bg-white flex'>
    <div class='w-7/12 px-8'>
      <settings-item-header :title="'Bots'"
                            :description="'Manage your bots here: create new ones, update existing ones, or get information.'"
                            :is-adding-new-item="isAddingNewBot"
                            class="border-none"
                            @click="isAddingNewBot ? cancelAddNewBot() : addNewBot()"
      />
      <with-loader id="settings-bots-table-loader" :loading="loaders.bots" class="h-full pb-10" use-as-overlay>
        <div v-if="isEmpty(bots)"
             class="h-3/5 flex flex-col justify-center items-center">
          <img :src="noBotsImage" alt="no bots image">
          <span class="text-center text-gray-dark mt-8">
            To start with Amio, just create a bot.
          </span>
        </div>
        <settings-bots-list v-else-if="!loaders.bots"
                            :bots="bots"
                            :multi-bots-by-id="multiBotsById"
                            :active-add-market-group-id="addMarketGroupId"
                            :active-add-channel-market-id="addChannelMarketId"
                            :selected-bot-index="selectedBotIndex"
                            :selected-panel-level="selectedPanelLevel"
                            :selected-channel="selectedBotChannel"
                            :is-rename-mode="isRenameMode"
                            @update:bot-name="saveBotName"
                            @request-rename="handleRequestRename"
                            @request-delete="handleRequestDelete"
                            @request-convert-to-multi-market="handleRequestConvertToMultiMarket"
                            @request-add-market="handleRequestAddMarket"
                            @request-add-channel="handleRequestAddChannel"
                            @select-bot-index="selectBotIndex"
                            @select-market-bot-index="selectMarketBotIndex"
                            @select-channel-index="selectChannelIndex"
                            @request-delete-market="handleRequestDeleteMarket"
                            class="h-5/6"
        />
      </with-loader>
    </div>

    <transition name="slide-fade" mode="out-in">
      <SettingsBotAddMarketEditor v-if="selectedBot && !selectedBotChannel && isAddingMarket"
                                  :bot="selectedBot"
                                  :multi-bots="multiBotsList"
                                  :is-loading="loaders.name"
                                  :key="`add-market-${selectedBot.id}`"
                                  @cancel="cancelAddMarket"
                                  @create="createMarket"
                                  class="w-5/12"
      />
      <SettingsBotAddChannelEditor v-else-if="selectedBot && !selectedBotChannel && isAddingChannel"
                                   :bot="selectedBot"
                                   :existing-channels="existingChannels"
                                   :is-loading="loaders.name"
                                   :key="`add-channel-${selectedBot.id}`"
                                   @cancel="cancelAddChannel"
                                   @create="createChannel"
                                   @deploy="deployChannel"
                                   class="w-5/12"
      />
      <SettingsBotMarketEditor v-else-if="selectedBot && !selectedBotChannel && isMarketSelected"
                               :bot="selectedBot"
                               :multi-bots="multiBotsList"
                               :key="`market-${selectedBot.id}`"
                               @request-delete-market="handleRequestDeleteMarket"
                               @display-bot-summary="showSelectedBotSummary"
                               class="w-5/12"
      />
      <SettingsBotSummary v-else-if="selectedBot && !selectedBotChannel"
                            :bot="selectedBot"
                            :key="`summary-${selectedBot.id}`"
                            :is-rename-mode="isRenameMode"
                            :is-loading="loaders.name"
                            @set-rename-mode="setRenameMode"
                            @rename="saveBotName"
                            @request-delete="handleRequestDelete"
                            @request-convert-to-multi-market="handleRequestConvertToMultiMarket"
                            @update-bot="updateBot"
                            @change-nlp-provider="changeNlpProvider"
                            class="w-5/12"
      />
      <settings-bot-editor v-else-if="selectedBot"
                           :bot="selectedBot"
                           :key="`editor-${selectedBot.id}`"
                           :is-rename-mode="isRenameMode"
                           :is-loading="loaders.name"
                           class="w-5/12"
                           :selected-channel="selectedBotChannel"
                           @set-rename-mode="setRenameMode"
                           @rename="saveBotName"
                           @request-delete="handleRequestDelete"
      />
      <settings-bot-create v-else-if="isAddingNewBot"
                           :multi-bots="multiBotsList"
                           @process-new-bot="processNewBot"
                           class="w-5/12"
      />
    </transition>

    <delete-dialog v-if="isDeleteDialogShown"
                   :is-shown="isDeleteDialogShown"
                   :item="deleteTarget"
                   :dialog-type="'prompt'"
                   :delete-title="deleteDialogTitle"
                   :delete-description="deleteDialogDescription"
                   :delete-confirm-text="deleteDialogConfirmText"
                   :input-placeholder="'Enter text'"
                   :required-prompt-value="requiredPromptValue"
                   @cancel="cancelDelete"
                   @delete="confirmDelete"
    >
      <template #delete-item-icon>
        <vs-icon v-if="deleteKind === 'market'"
                 icon="storefront"
                 size="2rem"
                 class="text-gray-dark"
        />
        <delete-bot-icon v-else class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>

    <settings-bot-convert-to-multi-market-dialog
      v-if="isConvertDialogShown"
      :is-shown="isConvertDialogShown"
      :bot="convertTarget"
      :multi-bots="multiBotsList"
      :is-loading="loaders.convert"
      @cancel="cancelConvertToMultiMarket"
      @convert="confirmConvertToMultiMarket"
    />
  </div>
</template>

<script>
import DeleteBotIcon from '@/assets/images/icons/DeleteBotIcon'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import SettingsItemHeader from '@/components/settings/SettingsItemHeader.vue'
import SettingsBotsList from '@/components/settings/bots/SettingsBotsList.vue'
import SettingsBotSummary from '@/components/settings/bots/SettingsBotSummary.vue'
import SettingsBotEditor from '@/components/settings/bots/SettingsBotEditor.vue'
import SettingsBotMarketEditor from '@/components/settings/bots/SettingsBotMarketEditor.vue'
import SettingsBotAddMarketEditor from '@/components/settings/bots/SettingsBotAddMarketEditor.vue'
import SettingsBotAddChannelEditor from '@/components/settings/bots/SettingsBotAddChannelEditor.vue'
import SettingsBotConvertToMultiMarketDialog from '@/components/settings/bots/SettingsBotConvertToMultiMarketDialog.vue'
import SettingsBotCreate from '@/components/settings/bots/SettingsBotCreate.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import botService from '@/services/amio/bot.service'
import channelService from '@/services/amio/channel.service'
import deploymentService from '@/services/amio/deployment.service'
import multiBotService from '@/services/amio/multi-bot.service'
import marketService from '@/services/amio/market.service'
import { resolveMarketId, marketNameForBot, multiBotNameForBot } from '@/utils/market.utils'
import { buildDeployableChannels } from '@/utils/channel.utils'
import { pollVariantJob } from '@/utils/variant-job.utils'
import { createTranslatingTypewriter } from '@/utils/typewriter'
import chatThemeService from '@/services/amio/chat-theme.service'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import findIndex from 'ramda/src/findIndex'
import isEmpty from 'ramda/src/isEmpty'
import prepend from 'ramda/src/prepend'
import propEq from 'ramda/src/propEq'
import update from 'ramda/src/update'
import equals from 'ramda/src/equals'
import isNil from 'ramda/src/isNil'
import type from 'ramda/src/type'
import filter from 'ramda/src/filter'
import map from 'ramda/src/map'
import pipe from 'ramda/src/pipe'
import reject from 'ramda/src/reject'
import anyPass from 'ramda/src/anyPass'
import toaster from '@/utils/toaster'
import { ROUTE_NAMES } from '@/router'
import { DEFAULT_APPEARANCE_JSON, DEFAULT_LANG_CONFIG } from '@/constants/chat-theme.constants'
import { ITEM_IDS } from '@/utils/editor.constants'
import { CHANNEL_TYPES } from '@/utils/channel.constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import noBotsImage from '@/assets/images/bots_empty.svg'

export default {
  name: 'SettingsBots',
  components: {
    DeleteBotIcon,
    DeleteDialog,
    SettingsItemHeader,
    SettingsBotsList,
    SettingsBotSummary,
    SettingsBotEditor,
    SettingsBotMarketEditor,
    SettingsBotAddMarketEditor,
    SettingsBotAddChannelEditor,
    SettingsBotConvertToMultiMarketDialog,
    SettingsBotCreate,
    WithLoader
  },

  watch: {
    '$route.params': {
      immediate: true,
      handler(params) {
        if (params.botId === ITEM_IDS.NEW) {
          this.selectedBotIndex = -1
          this.selectedChannelIndex = -1
          this.selectedPanelLevel = 'bot'
          this.pendingPanelLevel = null
          this.addMarketGroupId = null
          this.addChannelMarketId = null
        } else {
          this.selectBotIndexByRoute()
        }
      }
    },
    '$route.query.panel': {
      handler(panel) {
        if (panel === 'market' && this.$route.params.botId && this.$route.params.botId !== ITEM_IDS.NEW) {
          this.selectBotIndexByRoute()
        }
      }
    }
  },

  data() {
    return {
      noBotsImage,
      selectedBotIndex: -1,
      selectedChannelIndex: -1,
      selectedPanelLevel: 'bot',
      pendingPanelLevel: null,
      addMarketGroupId: null,
      addChannelMarketId: null,
      isRenameMode: false,
      loaders: {
        bots: false,
        name: false,
        convert: false
      },
      deleteTarget: null,
      deleteKind: 'bot',
      isDeleteDialogShown: false,
      convertTarget: null,
      isConvertDialogShown: false,
      isEmpty,
      multiBotsById: {},
      multiBotsByBotId: {},
      multiBotsList: [],
      // every channel in the org (bound or not), loaded together with the per-bot channels
      organizationChannels: []
    }
  },

  computed: {
    bots: botModule.getBotList,

    // The bot variant's own name (e.g. "Multibot") isn't what the user is confirming here — they're
    // confirming which MARKET they're detaching this bot from, so the type-to-confirm target and
    // the messaging should name the market, not the variant.
    deleteTargetMarketName() {
      return this.deleteTarget ? marketNameForBot(this.deleteTarget) : ''
    },

    // A variant's own name/multi_bot_name is seeded identically to its market's name at creation
    // (both come from the same "name - LANG" string), so it's redundant with — and can silently
    // diverge from — deleteTargetMarketName above. Never show the variant's own name; show the
    // shared multibot (parent/group) name instead, e.g. "Support Bot" rather than "Czech Market - CS".
    deleteTargetMultiBotName() {
      return this.deleteTarget ? multiBotNameForBot(this.deleteTarget) : ''
    },

    deleteDialogTitle() {
      if (!this.deleteTarget) return ''
      return this.deleteKind === 'market' ? `Remove Bot from "${this.deleteTargetMarketName}"?` : 'Delete Bot?'
    },

    isGroupDelete() {
      return this.deleteKind === 'bot' && !!this.deleteTarget?.multi_bot_id
    },

    deleteDialogDescription() {
      if (!this.deleteTarget) return ''
      if (this.deleteKind === 'market') {
        // This only removes this bot's variant/deployment in the market — the market entity
        // itself is never touched here, only via DELETE /markets/:marketId (Settings > Markets).
        return `This will stop "${this.deleteTargetMultiBotName}" from operating in "${this.deleteTargetMarketName}" and permanently delete its data. It cannot be undone.<br/><br/>The market itself will not be deleted — it stays available to attach other bots. Manage markets from Settings &gt; Markets.<br/><br/>Please type <strong>${this.deleteTargetMarketName}</strong> to enable delete.`
      }
      const cascadeWarning = this.isGroupDelete ?
        ' Variables scoped to this bot\'s markets will also be permanently deleted.' :
        ''
      return `This will delete all data for "${this.deleteTarget.name}".${cascadeWarning} It cannot be undone.<br/><br/>Please type <strong>${this.deleteTarget.name}</strong> to enable delete.`
    },

    deleteDialogConfirmText() {
      return this.deleteKind === 'market' ? 'Remove from Market' : 'Delete Bot'
    },

    requiredPromptValue() {
      if (!this.deleteTarget) return null
      return this.deleteKind === 'market' ? this.deleteTargetMarketName : this.deleteTarget.name
    },

    selectedBot() {
      return isEmpty(this.bots) && this.selectedBotIndex >= 0 ? null : this.bots[this.selectedBotIndex]
    },

    selectedBotChannel() {
      if (!this.selectedBot || this.selectedChannelIndex === -1) return null

      return this.selectedBot?.channels[this.selectedChannelIndex]
    },

    // Org channels the selected market could be deployed to (same channel family as the bot's type),
    // each bound one labelled with the bot/market it currently belongs to so the editor can show it as taken.
    existingChannels() {
      if (!this.selectedBot) return []
      return buildDeployableChannels(this.organizationChannels, this.bots, this.selectedBot)
        .map(channel => ({ ...channel, boundBotLabel: channel.boundBot ? this.botLabel(channel.boundBot) : null }))
    },

    isAddingNewBot() {
      return this.$route.path.includes(ITEM_IDS.NEW)
    },
    isMarketSelected() {
      return this.selectedPanelLevel === 'market'
    },
    isAddingMarket() {
      return this.selectedPanelLevel === 'addMarket'
    },
    isAddingChannel() {
      return this.selectedPanelLevel === 'addChannel'
    }
  },

  methods: {
    async attachChannels(bots) {
      try {
        const { channelsByBotId, failedBotIds, channels } = await channelService.channelListByBot()
        this.organizationChannels = channels
        const failed = new Set(failedBotIds)
        bots.forEach(bot => {
          bot.channels = channelsByBotId[bot.id] || []
          bot.channelsLoadFailed = failed.has(bot.id)
        })
        return bots.filter(bot => failed.has(bot.id))
      } catch (err) {
        this.organizationChannels = []
        bots.forEach(bot => {
          bot.channels = []
          bot.channelsLoadFailed = true
        })
        return bots
      }
    },

    // "Group / Market" for a market variant, plain bot name otherwise
    botLabel(bot) {
      return bot.multi_bot_id ? `${multiBotNameForBot(bot)} / ${marketNameForBot(bot)}` : bot.name
    },

    warnChannelsLoadFailed(failedBots) {
      if (!failedBots.length) return
      const names = failedBots.map(b => b.name || b.id).join(', ')
      toaster.warning(`Couldn't load channels for: ${names}. Some channels may be missing — try reloading.`)
    },

    addNewBot() {
      this.selectedBotIndex = -1
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'bot'
      this.pendingPanelLevel = null
      this.addMarketGroupId = null
      this.addChannelMarketId = null
      this.$router.push({ name: ROUTE_NAMES.bots, params: { botId: ITEM_IDS.NEW } })
    },

    cancelAddNewBot() {
      if (!isEmpty(this.bots)) {
        this.$router.push({
          name: ROUTE_NAMES.bots,
          params: {
            botId: this.bots[0].id
          }
        })
      } else {
        this.$router.push({
          name: ROUTE_NAMES.bots
        })
      }
    },

    selectBotIndex(botIndex) {
      if (this.selectedBotIndex !== botIndex) {
        this.selectedBotIndex = botIndex
      }
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'bot'
      this.pendingPanelLevel = 'bot'
      this.addMarketGroupId = null
      this.addChannelMarketId = null
    },

    selectMarketBotIndex(botIndex) {
      this.selectedBotIndex = botIndex
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'market'
      this.pendingPanelLevel = 'market'
      this.addMarketGroupId = null
      this.addChannelMarketId = null
    },
    showSelectedBotSummary() {
      if (this.selectedBotIndex < 0) return

      const primaryBot = botModule.resolvePrimaryBot(this.bots[this.selectedBotIndex].id)
      const primaryBotIndex = this.bots.findIndex(bot => bot.id === primaryBot.id)
      if (primaryBotIndex < 0) return

      this.selectBotIndex(primaryBotIndex)
      this.$router.push({
        name: ROUTE_NAMES.bots,
        params: {
          botId: primaryBot.id
        }
      })
    },

    selectChannelIndex(channelIndex) {
      this.selectedChannelIndex = channelIndex
      this.selectedPanelLevel = 'channel'
      this.pendingPanelLevel = 'channel'
      this.addMarketGroupId = null
      this.addChannelMarketId = null
    },
    handleRequestAddMarket(group) {
      if (this.addMarketGroupId === group.id) {
        this.cancelAddMarket()
        return
      }

      const groupPrimaryBotIndex = this.bots.findIndex(bot => bot.id === group.primaryBot.id)
      if (groupPrimaryBotIndex < 0) return

      this.addMarketGroupId = group.id
      this.addChannelMarketId = null
      this.selectedBotIndex = groupPrimaryBotIndex
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'addMarket'
      this.pendingPanelLevel = 'addMarket'
      this.$router.push({
        name: ROUTE_NAMES.bots,
        params: {
          botId: group.primaryBot.id
        }
      })
    },
    cancelAddMarket() {
      this.addMarketGroupId = null
      if (this.selectedBotIndex < 0) return

      this.selectBotIndex(this.selectedBotIndex)
    },
    selectCreatedMarketBot(bots, variantBotId) {
      if (!variantBotId) return

      const newBotIndex = bots.findIndex(bot => bot.id === variantBotId)
      if (newBotIndex < 0) return

      this.addMarketGroupId = null
      this.addChannelMarketId = null
      this.selectedBotIndex = newBotIndex
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'market'
      this.pendingPanelLevel = 'market'
      this.$router.push({
        name: ROUTE_NAMES.bots,
        params: {
          botId: variantBotId
        }
      })
    },
    handleRequestAddChannel(marketBot) {
      if (this.addChannelMarketId === marketBot.id) {
        this.cancelAddChannel()
        return
      }

      const marketBotIndex = this.bots.findIndex(bot => bot.id === marketBot.id)
      if (marketBotIndex < 0) return

      this.addChannelMarketId = marketBot.id
      this.addMarketGroupId = null
      this.selectedBotIndex = marketBotIndex
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'addChannel'
      this.pendingPanelLevel = 'addChannel'
      this.$router.push({
        name: ROUTE_NAMES.bots,
        params: {
          botId: marketBot.id
        }
      })
    },
    cancelAddChannel() {
      this.addChannelMarketId = null
      if (this.selectedBotIndex < 0) return
      this.selectMarketBotIndex(this.selectedBotIndex)
    },
    cleanObject(obj) {
      if (isNil(obj) || type(obj) !== 'Object') return obj

      const isFalsy = anyPass([isNil, equals(''), isEmpty])
      const cleanedEntries = pipe(
        Object.entries,
        reject(([key, value]) => isFalsy(value)),
        map(([key, value]) => {
          if (type(value) === 'Object') {
            const cleanedValue = this.cleanObject(value)
            return isEmpty(cleanedValue) ? null : [key, cleanedValue]
          }
          return [key, value]
        }),
        filter(entry => entry !== null)
      )(obj)

      return Object.fromEntries(cleanedEntries)
    },
    async upsertThemeForNewChannel(createPayload, createdChannel) {
      let chatThemeId = this.selectedBot?.chat_theme_id || null
      const selectedTheme = JSON.parse(JSON.stringify(createPayload.selectedTheme || {}))
      if (isEmpty(selectedTheme)) return chatThemeId

      if (createPayload.isTextOverrideEnabled && createdChannel?.id) {
        selectedTheme.langConfig = {
          ...(selectedTheme.langConfig || {}),
          [createdChannel.id]: {
            welcome: selectedTheme.langConfig?.default?.welcome || '',
            buttonTitle: selectedTheme.langConfig?.default?.buttonTitle || '',
            language: createPayload.selectedLanguage || 'en'
          }
        }
      }

      const cleanThemeConfig = selectedTheme.themeConfig ? this.cleanObject(selectedTheme.themeConfig) : DEFAULT_APPEARANCE_JSON
      const cleanLangConfig = selectedTheme.langConfig ? this.cleanObject(selectedTheme.langConfig) : DEFAULT_LANG_CONFIG
      const normalizedTheme = {
        ...selectedTheme,
        themeConfig: cleanThemeConfig,
        langConfig: cleanLangConfig
      }

      if (normalizedTheme.id) {
        if (!equals(normalizedTheme, createPayload.originalTheme || {})) {
          await chatThemeService.update(normalizedTheme)
        }
        return normalizedTheme.id
      }

      const createdTheme = await chatThemeService.create(normalizedTheme)
      return createdTheme.id
    },
    async createChannel(createPayload) {
      if (!this.selectedBot) return

      try {
        this.loaders.name = true
        const isDaktelaEmail = createPayload.type === CHANNEL_TYPES.EMAIL_DAKTELA
        const deployment = await deploymentService.createChannelAndDeployBot(
          this.selectedBot.id,
          isDaktelaEmail ? daktelaEmailDeploymentPayload(createPayload) : { name: createPayload.name }
        )
        const createdChannel = deployment?.channel
        if (!createdChannel?.id) return

        if (!isDaktelaEmail) {
          const chatThemeId = await this.upsertThemeForNewChannel(createPayload, createdChannel)
          if (chatThemeId && this.selectedBot.chat_theme_id !== chatThemeId) {
            await botService.update(this.selectedBot.id, {
              chat_theme_id: chatThemeId
            })
            await this.updateBot({
              ...this.selectedBot,
              chat_theme_id: chatThemeId
            })
          }
        }

        await this.showDeployedChannel(createdChannel.id)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.name = false
      }
    },
    // Deploy the selected market to a channel that already exists in the org. The engine rejects a
    // channel bound to another bot (422); the editor only offers unbound ones. Theme/webhook sync is server-side.
    async deployChannel(channelId) {
      if (!this.selectedBot) return

      try {
        this.loaders.name = true
        await deploymentService.deploy(this.selectedBot.id, channelId)
        await this.showDeployedChannel(channelId)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.name = false
      }
    },
    // refresh the selected bot's channels and open the newly deployed one
    async showDeployedChannel(channelId) {
      const refreshedChannels = await channelService.channelList(this.selectedBot.id)
      // a channel created via the Create tab is new to the org list too - keep it in sync so other
      // markets' Deploy tabs show it as taken
      const knownChannelIds = new Set(this.organizationChannels.map(channel => channel.id))
      this.organizationChannels = [...this.organizationChannels, ...refreshedChannels.filter(channel => !knownChannelIds.has(channel.id))]
      const updatedBots = this.bots.slice()
      updatedBots[this.selectedBotIndex] = {
        ...updatedBots[this.selectedBotIndex],
        channels: refreshedChannels
      }
      await botModule.setBotList(updatedBots)

      const newChannelIndex = refreshedChannels.findIndex(channel => channel.id === channelId)
      if (newChannelIndex < 0) return
      this.addChannelMarketId = null
      this.selectedPanelLevel = 'channel'
      this.pendingPanelLevel = 'channel'
      this.selectedChannelIndex = newChannelIndex

      await this.$router.push({
        name: ROUTE_NAMES.botsWithChannel,
        params: {
          botId: this.selectedBot.id,
          channelId
        }
      })
    },
    async createMarket(payload) {
      if (!this.selectedBot?.multi_bot_id) return

      // Close the Add market form as soon as Create is clicked — creation runs in the background and
      // its progress/result are surfaced via toasts. The new market is revealed afterwards.
      this.cancelAddMarket()

      let createdMarketId = null
      let createdMarketFlag = false
      let variantBotId = null
      let progressToastId = null
      let translatingAnim = null
      const stopAnim = () => { if (translatingAnim) { translatingAnim.stop(); translatingAnim = null } }
      try {
        this.loaders.name = true
        const multiBotId = this.selectedBot.multi_bot_id
        const { marketId, marketName, timezone, language, createdMarket } = await resolveMarketId(payload)
        createdMarketId = marketId
        createdMarketFlag = createdMarket

        const progressMessage = `Started creating a new market - ${marketName}, this will take a while...`
        progressToastId = toaster.info(progressMessage, { timeout: false, toastClassName: 'amio-progress-toast amio-fixed-width' })
        const variant = await multiBotService.createVariant(multiBotId, marketName, timezone, language, marketId)
        variantBotId = variant.primary_bot_id

        await pollVariantJob(
          () => multiBotService.getVariantProgress(multiBotId, variantBotId),
          {
            initialDelayMs: 5000,
            onUpdate: status => {
              if (!progressToastId) return
              if (!translatingAnim && status && status.step === 'translating-strings') {
                translatingAnim = createTranslatingTypewriter({
                  initialText: progressMessage,
                  onFrame: frame => { if (progressToastId) toaster.update(progressToastId, frame) }
                })
              }
              // pre-translating: keep the initial message; once the animation starts it owns the toast text.
            }
          }
        )

        // ── Variant is created from here on. Confirm success immediately. ──
        stopAnim()
        toaster.update(progressToastId, `Market "${marketName}" created`, { type: 'success', toastClassName: 'amio-success-toast amio-fixed-width', timeout: 20000, closeOnClick: true })
        progressToastId = null

        // Best-effort UI refresh — a failure here must NOT report a creation failure.
        try {
          const refreshedBots = await botService.listBots()
          const failed = await this.attachChannels(refreshedBots)
          refreshedBots.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
          await this.refreshMultiBotsList(refreshedBots)
          await botModule.setBotList(refreshedBots)
          this.warnChannelsLoadFailed(failed)

          // Reveal the freshly created market in the settings menu — but only if the user is still on
          // the bots settings. Creation can take a while; if they navigated to an unrelated page in
          // the meantime, don't yank them back. ($route is reactive and reflects their current
          // location even if this view was unmounted.)
          if (this.isBotsRoute()) this.selectCreatedMarketBot(refreshedBots, variantBotId)
        } catch (refreshErr) {
          console.error('Market created but refreshing the bot list failed; reload to see it.', refreshErr)
        }
      } catch (err) {
        stopAnim()
        const msg = (err && err.message) ? err.message : String(err)
        if (progressToastId) {
          toaster.update(progressToastId, msg, { type: 'error', toastClassName: 'amio-error-toast amio-fixed-width', timeout: false, closeOnClick: true })
          progressToastId = null
        } else {
          toaster.error(err)
        }
        // Only delete the market when we know no variant exists: the entity was never created
        // (createVariant threw before returning a botId), or the job definitively failed (the worker
        // already removed the partial variant). NOT on timeout/transient errors.
        const safeToDeleteMarket = createdMarketFlag && createdMarketId && (!variantBotId || err.jobFailed)
        if (safeToDeleteMarket) {
          try {
            await marketService.delete(createdMarketId)
          } catch (cleanupErr) {
            console.error('Failed to clean up market after variant creation failure', cleanupErr)
          }
        }
      } finally {
        stopAnim()
        if (progressToastId) toaster.dismiss(progressToastId)
        this.loaders.name = false
      }
    },

    isBotsRoute() {
      return this.$route.name === ROUTE_NAMES.bots || this.$route.name === ROUTE_NAMES.botsWithChannel
    },

    selectBotIndexByRoute() {
      if (isEmpty(this.bots)) return
      if (!this.isBotsRoute()) return
      const keepAddMarketState = this.pendingPanelLevel === 'addMarket'
      const keepAddChannelState = this.pendingPanelLevel === 'addChannel'
      if (!keepAddMarketState) this.addMarketGroupId = null
      if (!keepAddChannelState) this.addChannelMarketId = null

      const routeBotId = this.$route.params.botId
      const routeChannelId = this.$route.params.channelId

      if (routeBotId) {
        this.selectedBotIndex = this.bots.findIndex(bot => bot.id === routeBotId)

        if (this.selectedBotIndex >= 0) {
          const bot = this.bots[this.selectedBotIndex]

          if (routeChannelId) {
            this.selectedChannelIndex = (bot.channels || []).findIndex(channel => channel.id === routeChannelId)
            this.selectedPanelLevel = 'channel'
            this.addMarketGroupId = null
            this.addChannelMarketId = null
          } else {
            this.selectedChannelIndex = -1
            if (this.$route.query.panel === 'market') {
              this.selectedPanelLevel = 'market'
            } else {
              this.selectedPanelLevel = this.pendingPanelLevel || 'bot'
            }
          }
        }
        this.pendingPanelLevel = null
        return
      }

      this.$router.replace({
        name: ROUTE_NAMES.bots,
        params: {
          botId: this.bots[0].id
        }
      })
      this.selectedBotIndex = 0
      this.selectedChannelIndex = -1
      this.selectedPanelLevel = 'bot'
      this.pendingPanelLevel = null
    },

    setRenameMode(isRenameMode) {
      this.isRenameMode = isRenameMode
    },

    handleRequestDelete(bot) {
      this.deleteTarget = bot
      this.deleteKind = 'bot'
      this.isDeleteDialogShown = true
    },
    handleRequestDeleteMarket(bot) {
      this.deleteTarget = bot
      this.deleteKind = 'market'
      this.isDeleteDialogShown = true
    },
    handleRequestRename(bot) {
      const botIndex = this.bots.findIndex(item => item.id === bot.id)
      if (botIndex >= 0) this.selectBotIndex(botIndex)
      this.setRenameMode(true)
    },

    handleRequestConvertToMultiMarket(bot) {
      this.convertTarget = bot
      this.isConvertDialogShown = true
    },

    cancelConvertToMultiMarket() {
      this.convertTarget = null
      this.isConvertDialogShown = false
    },

    async confirmConvertToMultiMarket(payload) {
      if (!this.convertTarget) return

      this.loaders.convert = true
      try {
        const { marketId, marketName } = await resolveMarketId(payload)
        await multiBotService.createFromExistingBot(
          marketName,
          this.convertTarget.id,
          marketId
        )

        const refreshedBots = await botService.listBots()
        const failed = await this.attachChannels(refreshedBots)
        refreshedBots.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        await this.refreshMultiBotsList(refreshedBots)
        await botModule.setBotList(refreshedBots)
        this.warnChannelsLoadFailed(failed)

        const convertedBotId = this.convertTarget.id
        this.cancelConvertToMultiMarket()
        toaster.success(`"${refreshedBots.find(b => b.id === convertedBotId)?.name || 'Bot'}" converted to multi-market`)

        if (this.isBotsRoute()) {
          const botIndex = refreshedBots.findIndex(bot => bot.id === convertedBotId)
          if (botIndex >= 0) {
            this.selectedBotIndex = botIndex
            this.selectedChannelIndex = -1
            this.selectedPanelLevel = 'bot'
            this.pendingPanelLevel = 'bot'
            this.$router.push({
              name: ROUTE_NAMES.bots,
              params: { botId: convertedBotId }
            })
          }
        }
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.convert = false
      }
    },

    cancelDelete() {
      this.deleteTarget = null
      this.isDeleteDialogShown = false
    },

    async confirmDelete() {
      if (this.deleteKind === 'market') {
        await this.deleteMarketVariant()
      } else {
        await this.deleteBotInternal()
      }
    },

    async deleteMarketVariant() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await multiBotService.deleteVariant(this.deleteTarget.multi_bot_id, this.deleteTarget.id)
        const updatedBotList = this.bots.filter(bot => bot.id !== this.deleteTarget.id)
        await botModule.setBotList(updatedBotList)
        await this.refreshMultiBotsList(updatedBotList)
        if (isEmpty(updatedBotList)) {
          this.addNewBot()
        } else {
          const multiBotId = this.deleteTarget.multi_bot_id
          const remainingInGroup = updatedBotList.filter(b => b.multi_bot_id === multiBotId)
          const selectedBot = remainingInGroup[0] || updatedBotList[0]
          botModule.setBot(selectedBot)
          this.selectedBotIndex = updatedBotList.findIndex(b => b.id === selectedBot.id)
          await this.$router.replace({
            name: ROUTE_NAMES.bots,
            params: {
              botId: selectedBot.id
            }
          })
          this.selectedPanelLevel = 'bot'
          this.pendingPanelLevel = 'bot'
        }
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.deleteTarget = null
        this.isDeleteDialogShown = false
      }
    },

    async refreshMultiBotsList(bots) {
      try {
        const [
          multiBots,
          marketList
        ] = await Promise.all([
          multiBotService.list(),
          marketService.list()
        ])
        botModule.setBotVariantList(multiBots)
        marketModule.setMarketList(marketList)
        this.multiBotsList = multiBots
        const multiBotsById = {}
        const multiBotsByBotId = {}
        multiBots.forEach(multiBot => {
          multiBotsById[multiBot.id] = multiBot
          multiBotsByBotId[multiBot.primaryBotId] = multiBot
        })
        this.multiBotsById = multiBotsById
        this.multiBotsByBotId = multiBotsByBotId
        bots.forEach(bot => {
          bot.multi_bot_name = this.multiBotsByBotId[bot.id]?.name || ''
        })
      } catch (err) {
        this.multiBotsById = {}
        this.multiBotsByBotId = {}
        this.multiBotsList = []
      }
    },

    async deleteBotInternal() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        const deletedMultiBotId = this.deleteTarget?.multi_bot_id
        if (deletedMultiBotId) {
          await multiBotService.delete(deletedMultiBotId)
        } else {
          await botService.delete(this.deleteTarget.id)
        }
        const updatedBotList = deletedMultiBotId ?
          this.bots.filter(bot => bot.multi_bot_id !== deletedMultiBotId) :
          this.bots.filter(bot => bot.id !== this.deleteTarget.id)
        await botModule.setBotList(updatedBotList)
        await this.refreshMultiBotsList(updatedBotList)
        if (isEmpty(updatedBotList)) {
          this.addNewBot()
        } else {
          const selectedBot = updatedBotList[0]
          botModule.setBot(selectedBot)
          this.selectedBotIndex = 0
          // when a non-selected bot is deleted the URL may already point to selectedBot,
          // so only navigate if the target route actually changes (avoids NavigationDuplicated)
          if (this.$route.params.botId !== selectedBot.id) {
            await this.$router.replace({
              name: ROUTE_NAMES.bots,
              params: {
                botId: selectedBot.id
              }
            })
          }
        }
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.deleteTarget = null
        this.isDeleteDialogShown = false
      }
    },

    async processNewBot(newBot) {
      newBot.channels = await channelService.channelList(newBot.id)
      const updatedBotList = prepend(newBot, this.bots)
      await botModule.setBotList(updatedBotList)
      await this.refreshMultiBotsList(updatedBotList)
      botModule.setBot(newBot)
      await this.$router.replace({
        name: ROUTE_NAMES.bots,
        params: {
          botId: newBot.id
        }
      })
    },

    async saveBotName(updatedBot) {
      try {
        this.loaders.name = true
        await botService.update(updatedBot.id, { name: updatedBot.name })
        await this.updateBot(updatedBot)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.name = false
      }
    },

    async updateBot(updatedBot) {
      const index = findIndex(propEq('id', updatedBot.id))(this.bots)
      await botModule.setBotList(update(index, updatedBot, this.bots))
    },

    async changeNlpProvider(provider) {
      this.loaders.name = true
      try {
        // engine flips this bot (and, for a multi-bot, all its variants), then we refresh so the list reflects it
        await botService.setNlpProvider(this.selectedBot.id, provider)
        const refreshedBots = await botService.listBots()
        await this.refreshMultiBotsList(refreshedBots)
        const failed = await this.attachChannels(refreshedBots)
        this.warnChannelsLoadFailed(failed)
        refreshedBots.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        await botModule.setBotList(refreshedBots)
        toaster.success('Intent recognition updated')
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.name = false
      }
    }
  },

  async mounted() {
    this.loaders.bots = true
    const bots = await botService.listBots()
    if (isEmpty(bots)) {
      this.loaders.bots = false
      this.addNewBot()
      return
    }

    try {
      await this.refreshMultiBotsList(bots)
      const failed = await this.attachChannels(bots)
      this.warnChannelsLoadFailed(failed)
    } catch (err) {
      toaster.error(err)
    } finally {
      bots.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      await botModule.setBotList(bots)
      if (!this.$route.path.includes(ITEM_IDS.NEW)) this.selectBotIndexByRoute()
      this.loaders.bots = false
    }
  }
}

function daktelaEmailDeploymentPayload(createPayload) {
  const payload = {
    name: createPayload.name,
    type: createPayload.type,
    daktela_url: createPayload.daktela_url,
    access_token: createPayload.access_token,
    daktela_queue: createPayload.daktela_queue,
    daktela_ticket_category: createPayload.daktela_ticket_category
  }
  if (createPayload.daktela_queue_status) payload.daktela_queue_status = createPayload.daktela_queue_status
  if (createPayload.daktela_processed_category) payload.daktela_processed_category = createPayload.daktela_processed_category
  if (createPayload.daktela_user_name) payload.daktela_user_name = createPayload.daktela_user_name
  return payload
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
